import { useSEO } from "@/hooks/useSEO";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import VehicleCard from "@/components/VehicleCard";
import { motion } from "framer-motion";
import { ArrowDownWideNarrow, Sparkles } from "lucide-react";

const CATEGORIES = ["All", "SUV", "Sedan", "Compact", "Convertible", "Van", "Luxury", "Truck"];
const CATEGORY_STYLES = {
  All: "bg-primary text-primary-foreground border-primary shadow-[0_4px_14px_rgba(199,30,46,0.35)]",
  SUV: "bg-[#1A1A1A] text-white border-[#1A1A1A]",
  Sedan: "bg-[#1A1A1A] text-white border-[#1A1A1A]",
  Compact: "bg-[#1A1A1A] text-white border-[#1A1A1A]",
  Convertible: "bg-[#1A1A1A] text-white border-[#1A1A1A]",
  Van: "bg-[#1A1A1A] text-white border-[#1A1A1A]",
  Luxury: "bg-[#1A1A1A] text-white border-[#1A1A1A]",
  Truck: "bg-[#1A1A1A] text-white border-[#1A1A1A]",
};

export default function Fleet() {
  useSEO({ title: "Bahamas Car Hire Fleet — From $55/day | Easy Drive Nassau", description: "Browse the Easy Drive fleet in Nassau: compact cars from $55/day, sedans, SUVs, vans and trucks. Unlimited mileage on New Providence, free delivery included.", path: "/fleet" });
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("All");
  const [sort, setSort] = useState("featured");
  const [scarcity, setScarcity] = useState(null);
  const [searchParams] = useSearchParams();
  const query = searchParams.toString() ? `?${searchParams.toString()}` : "";

  const pickupDate = searchParams.get("pickup_date");
  const dropoffDate = searchParams.get("dropoff_date");
  const rentalDays =
    pickupDate && dropoffDate
      ? Math.round((new Date(dropoffDate) - new Date(pickupDate)) / 86400000)
      : 0;
  const freeBabySeat = rentalDays >= 12;

  useEffect(() => {
    api
      .get("/vehicles")
      .then((r) => setVehicles(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get("/weekend-scarcity").then((r) => setScarcity(r.data)).catch(() => {});
  }, []);

  const filtered = (cat === "All" ? vehicles : vehicles.filter((v) => v.category === cat))
    .slice()
    .sort((a, b) => {
      if (sort === "price-asc") return a.price_per_day - b.price_per_day;
      if (sort === "price-desc") return b.price_per_day - a.price_per_day;
      if (sort === "seats") return b.seats - a.seats;
      if (sort === "name") return a.name.localeCompare(b.name);
      if (sort === "category") return a.category.localeCompare(b.category) || a.name.localeCompare(b.name);
      return 0;
    });
  const cats = CATEGORIES.filter((c) => c === "All" || vehicles.some((v) => v.category === c));

  const fromPrice = vehicles.length ? Math.min(...vehicles.map((v) => v.price_per_day)) : null;
  const maxSeats = vehicles.length ? Math.max(...vehicles.map((v) => v.seats || 0)) : null;

  return (
    <div data-testid="fleet-page" className="pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="text-xs font-ui tracking-[0.35em] uppercase text-primary flex items-center gap-2">
            <Sparkles size={14} /> Easy Drive Fleet · Nassau, Bahamas
          </p>
          <h1 className="mt-5 font-display text-6xl sm:text-7xl lg:text-8xl tracking-tight leading-[0.95]">
            Pick your<br /><span className="italic gold-text">island ride</span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed font-ui">
            Every vehicle includes basic insurance, unlimited mileage on New Providence,
            and airport drop-off when you fly out.
          </p>
          {vehicles.length > 0 && (
            <div data-testid="fleet-stats" className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm font-ui">
              <span><span className="font-display text-2xl text-foreground">{vehicles.length}</span> <span className="text-muted-foreground">vehicles ready</span></span>
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
              <span><span className="font-display text-2xl gold-text">${fromPrice}</span> <span className="text-muted-foreground">/day from</span></span>
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
              <span><span className="font-display text-2xl text-foreground">{maxSeats}</span> <span className="text-muted-foreground">max seats</span></span>
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          data-testid="fleet-weekly-banner"
          className="mt-10 inline-flex items-center gap-3 rounded-full border border-primary/30 bg-primary/5 px-6 py-3.5"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground font-display text-sm">%</span>
          <p className="text-base font-ui text-foreground/85">
            <span className="gold-text font-semibold">Rent 7+ days, save 10%</span> — weekly discount applied automatically at checkout
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
          className="mt-10 rounded-3xl border border-border bg-white/70 backdrop-blur-xl shadow-[0_8px_30px_rgba(0,0,0,0.05)] px-6 py-5 flex flex-wrap items-center justify-between gap-4"
        >
          <div data-testid="fleet-category-filters" className="flex flex-wrap gap-2.5">
            {cats.map((c) => (
              <button
                key={c}
                data-testid={`fleet-filter-${c.toLowerCase()}`}
                onClick={() => setCat(c)}
                className={`rounded-full px-5 py-2 text-sm font-ui border transition-all duration-200 ${
                  cat === c
                    ? CATEGORY_STYLES[c] || CATEGORY_STYLES.All
                    : "border-border text-muted-foreground hover:text-primary hover:border-primary/40 hover:-translate-y-0.5"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <ArrowDownWideNarrow size={15} className="text-muted-foreground" />
            <select data-testid="fleet-sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="h-10 rounded-full border border-input bg-white px-4 text-sm font-ui hover:border-primary/50 transition-colors duration-200 cursor-pointer">
              <option value="featured">Featured</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="seats">Most Seats</option>
              <option value="name">Name: A to Z</option>
              <option value="category">Category</option>
            </select>
          </div>
        </motion.div>

        {loading ? (
          <p className="mt-16 text-muted-foreground font-ui">Loading fleet…</p>
        ) : filtered.length === 0 ? (
          <p data-testid="fleet-empty-message" className="mt-16 text-muted-foreground font-ui">No vehicles in this category right now.</p>
        ) : (
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((v, i) => (
              <div key={v.id} className={`fade-up fade-up-${(i % 4) + 1}`}>
                <VehicleCard
                  vehicle={v}
                  query={query}
                  freeBabySeat={freeBabySeat}
                  bestValue={!!v.badge}
                  weekendNote={scarcity && scarcity.available_count <= 3
                    ? (scarcity.available_ids.includes(v.id)
                        ? `Only ${scarcity.available_count} car${scarcity.available_count === 1 ? "" : "s"} left this weekend`
                        : "Booked this weekend")
                    : null}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
